import { useMemo } from "react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useDarkMode } from "../../hooks/useDarkMode";

/**
 * Reusable table-shaped skeleton for DataTables / list sections.
 * Safe drop-in for progressComponent and early-return loading states.
 * Accepts `columns` as a number or as an array of column definitions
 * to dynamically mirror full column widths across horizontal scrolling tables.
 */
const TableSkeleton = ({
  rows = 8,
  columns = 6,
  showHeader = false,
  className = "",
  minHeight = 200,
  selectableRows = false,
}) => {
  const isDarkMode = useDarkMode();
  const widths = [90, 140, 110, 100, 120, 130, 100, 80];
  const isColumnArray = Array.isArray(columns);
  const hasAnyFixedColWidth =
    isColumnArray &&
    columns.filter((c) => {
      if (!c || typeof c !== "object") return false;
      const isAction =
        c.id === "action" ||
        c.name === "Action" ||
        (typeof c.name === "string" && c.name.toLowerCase().includes("action"));
      return !isAction && Boolean(c.width) && !String(c.width).endsWith("%");
    }).length >= 3;

  const colList = useMemo(() => {
    let list = [];
    if (isColumnArray) {
      list = columns.map((col, idx) => {
        if (typeof col === "object" && col !== null) {
          const hasExplicitWidth = Boolean(col.width);
          const width = col.width;
          const minWidth = col.minWidth || (hasExplicitWidth ? undefined : "100px");
          const maxWidth = col.maxWidth;
          const grow =
            col.grow !== undefined ? col.grow : hasExplicitWidth ? 0 : 1;
          const numericWidth =
            typeof width === "number"
              ? width
              : typeof width === "string"
              ? parseInt(width, 10)
              : typeof minWidth === "number"
              ? minWidth
              : typeof minWidth === "string"
              ? parseInt(minWidth, 10)
              : null;
          const isAction =
            col.id === "action" ||
            col.name === "Action" ||
            (typeof col.name === "string" &&
              col.name.toLowerCase().includes("action"));

          const buttons = col.buttons || col.actionCount;

          return {
            key: col.id || col.name || idx,
            id: col.id,
            name: col.name,
            width,
            minWidth,
            maxWidth,
            grow,
            hasExplicitWidth,
            numericWidth: !isNaN(numericWidth) ? numericWidth : null,
            center: Boolean(col.center),
            right: Boolean(col.right),
            isAction,
            buttons,
            isBadge: Boolean(col.isBadge),
          };
        }
        const numeric =
          typeof col === "number"
            ? col
            : typeof col === "string"
            ? parseInt(col, 10)
            : null;
        return {
          key: idx,
          id: idx,
          width: col,
          minWidth: col,
          hasExplicitWidth: true,
          grow: 0,
          numericWidth: !isNaN(numeric) ? numeric : null,
          center: false,
          right: false,
          isAction: false,
          isBadge: false,
        };
      });
    } else {
      const count = typeof columns === "number" ? columns : 6;
      list = Array.from({ length: count }).map((_, i) => ({
        key: i,
        id: i,
        width: widths[i % widths.length],
        minWidth: widths[i % widths.length],
        hasExplicitWidth: true,
        grow: 0,
        numericWidth: widths[i % widths.length],
        center: false,
        right: false,
        isAction: false,
        isStatus: i === 3,
        isBadge: false,
      }));
    }

    if (selectableRows) {
      list = [
        {
          key: "__selectable__",
          id: "__selectable__",
          name: "",
          width: "48px",
          minWidth: "48px",
          maxWidth: "48px",
          grow: 0,
          hasExplicitWidth: true,
          numericWidth: 48,
          center: true,
          right: false,
          isAction: false,
          isCheckbox: true,
        },
        ...list,
      ];
    }

    return list;
  }, [columns, isColumnArray, selectableRows]);

  const renderCellSkeleton = (col, rowIndex = 0) => {
    if (col.isCheckbox) {
      return (
        <div className="d-flex align-items-center justify-content-center w-100">
          <Skeleton height={18} width={18} borderRadius={4} />
        </div>
      );
    }

    const colKey = String(col.id || col.name || "").toLowerCase();

    if (col.isAction) {
      const showThreeButtons = col.buttons
        ? col.buttons >= 3
        : col.numericWidth && col.numericWidth >= 240;
      return (
        <div className="d-flex align-items-center justify-content-center gap-2 w-100">
          {showThreeButtons && (
            <Skeleton height={28} width={32} borderRadius={8} />
          )}
          <Skeleton height={28} width={64} borderRadius={8} />
          <Skeleton height={28} width={72} borderRadius={8} />
        </div>
      );
    }

    if (
      col.isStatus ||
      col.isBadge ||
      (colKey.includes("status") && !colKey.includes("error"))
    ) {
      return <Skeleton height={22} width={72} borderRadius={40} />;
    }

    let skelWidth;

    if (colKey.includes("master")) {
      skelWidth = 56 + (rowIndex % 3) * 6;
    } else if (colKey.includes("role")) {
      skelWidth = 66 + (rowIndex % 3) * 6;
    } else if (
      colKey.includes("user") ||
      colKey.includes("systemid") ||
      colKey === "system" ||
      colKey.includes("client")
    ) {
      skelWidth = 80 + (rowIndex % 4) * 12;
    } else if (colKey.includes("smsc")) {
      skelWidth = 78 + (rowIndex % 3) * 8;
    } else if (colKey.includes("network") || colKey.includes("operator")) {
      skelWidth = 85 + (rowIndex % 4) * 16;
    } else if (colKey.includes("reset")) {
      skelWidth = 65 + (rowIndex % 2) * 8;
    } else if (colKey.includes("route")) {
      skelWidth = 72 + (rowIndex % 3) * 8;
    } else if (colKey.includes("country")) {
      skelWidth = 75 + (rowIndex % 3) * 10;
    } else if (colKey.includes("duration")) {
      skelWidth = 48 + (rowIndex % 3) * 6;
    } else if (colKey.includes("percent")) {
      skelWidth = 42 + (rowIndex % 2) * 6;
    } else if (colKey.includes("hold") || colKey.includes("traffic")) {
      skelWidth = 46 + (rowIndex % 2) * 6;
    } else if (colKey.includes("remark")) {
      skelWidth = 110 + (rowIndex % 3) * 15;
    } else if (colKey.includes("sender")) {
      // 6-character sender headers (e.g. HDFCBK)
      skelWidth = 58 + (rowIndex % 3) * 6;
    } else if (
      colKey.includes("peid") ||
      colKey.includes("principle") ||
      colKey.includes("entity")
    ) {
      // 19-digit numerical Principle Entity ID (e.g. 1201159123456789012)
      skelWidth = 112 + (rowIndex % 3) * 6;
    } else if (colKey.includes("hash")) {
      // Alphanumeric Hash ID
      skelWidth = 145 + (rowIndex % 4) * 14;
    } else if (colKey.includes("templateid")) {
      // 19-digit numerical Template ID
      skelWidth = 115 + (rowIndex % 3) * 6;
    } else if (
      colKey.includes("codedec") ||
      colKey.includes("decimal") ||
      colKey === "dec" ||
      colKey.endsWith("dec")
    ) {
      // Decimal error code numbers (e.g. 0, 1, 64, 1024)
      skelWidth = 36 + (rowIndex % 3) * 6;
    } else if (
      colKey.includes("codehex") ||
      colKey.includes("hex")
    ) {
      // 10-char Hex error code (e.g. 0x00000000)
      skelWidth = 76 + (rowIndex % 2) * 6;
    } else if (colKey.includes("shortcode")) {
      // 5-6 digit short code (e.g. 56767, 56161)
      skelWidth = 48 + (rowIndex % 2) * 8;
    } else if (colKey.includes("prefix")) {
      // Short prefix keywords (e.g. HELP, START, STOP)
      skelWidth = 46 + (rowIndex % 3) * 6;
    } else if (colKey.includes("suffix")) {
      // Short suffix keywords
      skelWidth = 44 + (rowIndex % 2) * 8;
    } else if (colKey.includes("expire")) {
      // Expiration date
      skelWidth = 98 + (rowIndex % 2) * 10;
    } else if (colKey.includes("delivery")) {
      // Delivery ID
      skelWidth = 140 + (rowIndex % 3) * 15;
    } else if (colKey.includes("source")) {
      // Phone / Source address
      skelWidth = 92 + (rowIndex % 3) * 8;
    } else if (colKey.includes("replied") || colKey === "reply") {
      // True / False reply status
      skelWidth = 42 + (rowIndex % 2) * 8;
    } else if (colKey === "mcc" || colKey === "mnc") {
      // 2-3 digit MCC / MNC code (e.g. 404, 01)
      skelWidth = 32 + (rowIndex % 2) * 6;
    } else if (
      colKey === "id" ||
      colKey === "no" ||
      colKey === "srno" ||
      colKey.endsWith("srno")
    ) {
      // Row ID / Sequence number
      skelWidth = 38 + (rowIndex % 3) * 6;
    } else if (colKey.includes("particular") || colKey.includes("crdr")) {
      // Cr / Dr token
      skelWidth = 35 + (rowIndex % 2) * 8;
    } else if (colKey.includes("balance")) {
      // Balance numbers with decimals
      skelWidth = 72 + (rowIndex % 3) * 10;
    } else if (colKey.includes("term")) {
      // Days term (e.g. 30 days)
      skelWidth = 55 + (rowIndex % 2) * 8;
    } else if (colKey.includes("paytype") || colKey.includes("paymenttype")) {
      // Payment type (Prepaid / Postpaid)
      skelWidth = 58 + (rowIndex % 2) * 8;
    } else if (colKey.includes("paymethod") || colKey.includes("method")) {
      // Payment method (Cash, Online, etc.)
      skelWidth = 65 + (rowIndex % 3) * 8;
    } else if (colKey.includes("approve")) {
      // Approved by username
      skelWidth = 70 + (rowIndex % 3) * 10;
    } else if (colKey.includes("actionfrom")) {
      skelWidth = 65 + (rowIndex % 3) * 8;
    } else if (colKey.includes("added")) {
      // Added by / Added to username
      skelWidth = 75 + (rowIndex % 3) * 10;
    } else if (colKey.includes("time")) {
      // Date and time string
      skelWidth = 105 + (rowIndex % 2) * 10;
    } else if (colKey.includes("errorname") || colKey.includes("error")) {
      // SMPP Error Name (e.g. ESME_ROK, ESME_RINVMSGLEN)
      skelWidth = 105 + (rowIndex % 3) * 12;
    } else if (colKey.includes("flag")) {
      skelWidth = 48 + (rowIndex % 3) * 8;
    } else if (colKey === "type" || colKey.endsWith("type")) {
      // Error type (e.g. SMPP, SYSTEM)
      skelWidth = 50 + (rowIndex % 3) * 6;
    } else if (colKey.includes("description") || colKey.includes("desc")) {
      // Error description sentences
      skelWidth = 165 + (rowIndex % 4) * 20;
    } else if (colKey.includes("template") || colKey.includes("message")) {
      // Template content / text
      skelWidth = 160 + (rowIndex % 4) * 20;
    } else if (colKey.includes("title")) {
      skelWidth = 90 + (rowIndex % 3) * 15;
    } else if (colKey.includes("language")) {
      skelWidth = 52 + (rowIndex % 2) * 10;
    } else if (colKey.includes("category")) {
      skelWidth = 80 + (rowIndex % 2) * 10;
    } else if (
      colKey.includes("date") ||
      colKey.includes("created") ||
      colKey.includes("updated")
    ) {
      skelWidth = 95 + (rowIndex % 2) * 10;
    } else if (
      colKey.includes("phone") ||
      colKey.includes("mobile") ||
      colKey.includes("contact")
    ) {
      skelWidth = 92 + (rowIndex % 3) * 6;
    } else if (colKey.includes("email")) {
      skelWidth = 125 + (rowIndex % 3) * 10;
    } else if (colKey.includes("ip") || colKey.includes("port")) {
      skelWidth = 65 + (rowIndex % 3) * 8;
    } else if (
      colKey.includes("credit") ||
      colKey.includes("amount") ||
      colKey.includes("limit") ||
      colKey.includes("rate") ||
      colKey.includes("price") ||
      colKey.includes("selling") ||
      colKey.includes("cost")
    ) {
      skelWidth = 55 + (rowIndex % 3) * 10;
    } else if (col.hasExplicitWidth && col.numericWidth) {
      skelWidth = Math.max(
        40,
        Math.min(col.numericWidth - 28, Math.round(col.numericWidth * 0.72))
      );
    } else if (col.grow && col.grow > 1) {
      skelWidth = Math.min(130, 85 + (rowIndex % 4) * 12);
    } else if (col.grow) {
      skelWidth = Math.min(100, 65 + (rowIndex % 3) * 12);
    } else {
      skelWidth = 70;
    }

    return <Skeleton height={14} width={skelWidth} borderRadius={4} />;
  };

  return (
    <SkeletonTheme
      baseColor={isDarkMode ? "#2b3035" : "#e9ecef"}
      highlightColor={isDarkMode ? "#3a4046" : "#f8f9fa"}
    >
      <div
        className={`common-table-skeleton ${isColumnArray ? "common-table-skeleton--matching-cols" : ""} ${className}`.trim()}
        style={{
          minHeight,
          width: "100%",
          minWidth: hasAnyFixedColWidth ? "max-content" : "100%",
        }}
        aria-busy="true"
        aria-label="Loading"
        role="status"
      >
        {showHeader && (
          <div
            className="common-table-skeleton__header"
            style={
              isColumnArray
                ? {
                    gap: 0,
                    padding: 0,
                    display: "flex",
                    alignItems: "stretch",
                    minWidth: hasAnyFixedColWidth ? "max-content" : "100%",
                    width: "100%",
                  }
                : undefined
            }
          >
            {colList.map((col, i) => (
              <div
                key={`h-${col.key || i}`}
                className="common-table-skeleton__cell"
                style={
                  isColumnArray
                    ? {
                        width: col.hasExplicitWidth ? col.width : undefined,
                        minWidth: col.minWidth,
                        maxWidth: col.maxWidth,
                        flexGrow: col.grow,
                        flexShrink: 0,
                        flexBasis: col.hasExplicitWidth ? col.width : "0px",
                        boxSizing: "border-box",
                        padding: hasAnyFixedColWidth ? "8px" : "8px 16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: col.center
                          ? "center"
                          : col.right
                          ? "flex-end"
                          : "flex-start",
                      }
                    : { minWidth: col.minWidth }
                }
              >
                {col.isCheckbox ? (
                  <Skeleton height={18} width={18} borderRadius={4} />
                ) : (
                  <Skeleton
                    height={12}
                    width={
                      col.numericWidth
                        ? Math.max(30, Math.min(col.numericWidth - 32, 80))
                        : 60
                    }
                  />
                )}
              </div>
            ))}
          </div>
        )}
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div
            key={rowIndex}
            className="common-table-skeleton__row"
            style={
              isColumnArray
                ? {
                    display: "flex",
                    alignItems: "center",
                    minWidth: hasAnyFixedColWidth ? "max-content" : "100%",
                    width: "100%",
                    padding: 0,
                    gap: 0,
                    minHeight: 48,
                  }
                : undefined
            }
          >
            {colList.map((col) => (
              <div
                key={col.key}
                className="common-table-skeleton__cell"
                style={
                  isColumnArray
                    ? {
                        width: col.hasExplicitWidth ? col.width : undefined,
                        minWidth: col.minWidth,
                        maxWidth: col.maxWidth,
                        flexGrow: col.grow,
                        flexShrink: 0,
                        flexBasis: col.hasExplicitWidth ? col.width : "0px",
                        boxSizing: "border-box",
                        padding: hasAnyFixedColWidth ? "6px 8px" : "6px 16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: col.center
                          ? "center"
                          : col.right
                          ? "flex-end"
                          : "flex-start",
                      }
                    : { minWidth: col.minWidth }
                }
              >
                {renderCellSkeleton(col, rowIndex)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </SkeletonTheme>
  );
};

export const FormSkeleton = ({ rows = 5, className = "" }) => {
  const isDarkMode = useDarkMode();

  return (
    <SkeletonTheme
      baseColor={isDarkMode ? "#2b3035" : "#e9ecef"}
      highlightColor={isDarkMode ? "#3a4046" : "#f8f9fa"}
    >
      <div
        className={`common-form-skeleton ${className}`.trim()}
        aria-busy="true"
        aria-label="Loading"
        role="status"
        style={{ width: "100%", maxWidth: 480, padding: 12 }}
      >
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="mb-3">
            <Skeleton height={12} width="30%" className="mb-2" />
            <Skeleton height={36} borderRadius={8} />
          </div>
        ))}
        <div className="d-flex justify-content-end gap-2 mt-2">
          <Skeleton height={36} width={88} borderRadius={8} />
          <Skeleton height={36} width={88} borderRadius={8} />
        </div>
      </div>
    </SkeletonTheme>
  );
};

export const CompactSkeleton = ({ className = "" }) => {
  const isDarkMode = useDarkMode();

  return (
    <SkeletonTheme
      baseColor={isDarkMode ? "#2b3035" : "#e9ecef"}
      highlightColor={isDarkMode ? "#3a4046" : "#f8f9fa"}
    >
      <div
        className={className}
        aria-busy="true"
        aria-label="Loading"
        role="status"
      >
        <Skeleton height={20} width={80} borderRadius={6} />
      </div>
    </SkeletonTheme>
  );
};

/** Chart body skeleton — keeps card headers visible; only the plot area pulses. */
export const ChartSkeleton = ({
  height = 320,
  variant = "bar",
  className = "",
}) => {
  const isDarkMode = useDarkMode();
  const barHeights = [160, 220, 120, 260, 180];
  const columnPercents = [22, 18, 88, 42, 58];
  const rowPercents = [48, 32, 58, 28, 86];

  return (
    <SkeletonTheme
      baseColor={isDarkMode ? "#2b3035" : "#e9ecef"}
      highlightColor={isDarkMode ? "#3a4046" : "#f8f9fa"}
    >
      <div
        className={`common-chart-skeleton ${className}`.trim()}
        style={{ minHeight: height, height }}
        aria-busy="true"
        aria-label="Loading chart"
        role="status"
      >
        {variant === "pie" ? (
          <div className="common-chart-skeleton__pie">
            <Skeleton circle width={180} height={180} />
            <div className="common-chart-skeleton__legend">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="d-flex align-items-center gap-2 mb-2">
                  <Skeleton width={14} height={14} borderRadius={3} />
                  <Skeleton height={12} width={90} />
                </div>
              ))}
            </div>
          </div>
        ) : variant === "column" ? (
          <div className="common-chart-skeleton__graph">
            <div className="common-chart-skeleton__plot">
              <div className="common-chart-skeleton__y-axis" aria-hidden="true">
                {["100%", "75%", "50%", "25%", "0%"].map((tick) => (
                  <span key={tick}>{tick}</span>
                ))}
              </div>
              <div className="common-chart-skeleton__plot-body">
                <div className="common-chart-skeleton__grid" aria-hidden="true">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} />
                  ))}
                </div>
                <div className="common-chart-skeleton__columns">
                  {columnPercents.map((pct, i) => (
                    <div key={i} className="common-chart-skeleton__column">
                      <div
                        className="common-chart-skeleton__column-bar"
                        style={{ height: `${pct}%` }}
                      >
                        <Skeleton height="100%" borderRadius="6px 6px 0 0" />
                      </div>
                      <Skeleton height={8} width={36} className="mt-2" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="common-chart-skeleton__legend-row">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="d-flex align-items-center gap-2">
                  <Skeleton width={12} height={12} borderRadius={2} />
                  <Skeleton height={10} width={52} />
                </div>
              ))}
            </div>
          </div>
        ) : variant === "barH" ? (
          <div className="common-chart-skeleton__graph">
            <div className="common-chart-skeleton__h-plot">
              <div className="common-chart-skeleton__h-rows">
                {rowPercents.map((pct, i) => (
                  <div key={i} className="common-chart-skeleton__h-row">
                    <Skeleton height={10} width={44} />
                    <div className="common-chart-skeleton__h-track">
                      <div
                        className="common-chart-skeleton__h-bar"
                        style={{ width: `${pct}%` }}
                      >
                        <Skeleton height={18} borderRadius={4} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="common-chart-skeleton__x-axis" aria-hidden="true">
                {["0%", "25%", "50%", "75%", "100%"].map((tick) => (
                  <span key={tick}>{tick}</span>
                ))}
              </div>
            </div>
            <div className="common-chart-skeleton__legend-row">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="d-flex align-items-center gap-2">
                  <Skeleton width={12} height={12} borderRadius={2} />
                  <Skeleton height={10} width={44} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="common-chart-skeleton__bars">
            {barHeights.map((barHeight, i) => (
              <div key={i} className="common-chart-skeleton__bar-col">
                <Skeleton height={barHeight} width="70%" borderRadius={6} />
                <Skeleton height={10} width="80%" className="mt-2" />
              </div>
            ))}
          </div>
        )}
      </div>
    </SkeletonTheme>
  );
};

export default TableSkeleton;
